import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import GradientButton from '../ui/GradientButton';

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Smooth spring physics
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // Parallax transforms
  const y = useTransform(smoothProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(smoothProgress, [0, 0.5, 1], [1, 0.5, 0]);
  const scale = useTransform(smoothProgress, [0, 1], [1, 0.8]);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={sectionRef}
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden" 
      style={{ background: 'transparent' }}
    >
      <motion.div
        style={{ y, opacity, scale }}
        className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8 pt-32 pb-20 w-full text-center"
      >
        {/* Main Headline with premium font */}
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-[clamp(3.5rem,9vw,8rem)] leading-[0.9] font-bold tracking-[-0.05em] text-white mb-8"
          style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif", fontWeight: 800 }}
        >
          <motion.span 
            className="block"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Transform Ideas
          </motion.span>
          <motion.span
            className="block bg-gradient-to-r from-[#00D4FF] via-[#0099FF] to-[#0066FF] bg-clip-text text-transparent"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            style={{ 
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Into Reality
          </motion.span>
        </motion.h1>

        {/* Subtitle with letter spacing and fade in */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-[#B4B4C6] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-12"
          style={{ fontFamily: "'Inter', -apple-system, sans-serif", fontWeight: 400, letterSpacing: '0.01em' }}
        >
          We build cutting-edge <motion.span 
            className="text-[#00D4FF] font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >AI systems</motion.span>, <motion.span 
            className="text-[#0099FF] font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >blockchain solutions</motion.span>, and <motion.span 
            className="text-[#0066FF] font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
          >immersive experiences</motion.span> that push boundaries.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="flex flex-wrap gap-4 justify-center"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <GradientButton onClick={() => scrollTo('#contact')} className="!rounded-full !px-8 !py-4">
              Start Your Project <ArrowRight className="w-5 h-5" />
            </GradientButton>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <GradientButton variant="ghost" onClick={() => scrollTo('#services')} className="!rounded-full !px-8 !py-4">
              View Our Work
            </GradientButton>
          </motion.div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-20 flex flex-wrap gap-10 justify-center"
        >
          {[
            { value: '50+', label: 'Projects Delivered' },
            { value: '99.9%', label: 'Client Satisfaction' },
            { value: '24/7', label: 'Support Available' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.4 + i * 0.1, type: 'spring' }}
              whileHover={{ y: -5 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold gradient-text mb-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800 }}>
                {stat.value}
              </div>
              <div className="text-[#4A4A65] text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
